import type { CSSProperties } from "react";

export type SubjectTreeNodeStyle = "classic" | "acrylic" | "oil" | "watercolor";

export const SUBJECT_TREE_STYLE_STORAGE_KEY = "gistoscope.subjectTree.nodeStyle";
export const DEFAULT_SUBJECT_TREE_STYLE: SubjectTreeNodeStyle = "watercolor";

const SUBJECT_TREE_BRUSH_VARIANT_COUNT = 12;

export const subjectTreeStyleOptions: Array<{ value: SubjectTreeNodeStyle; label: string }> = [
  { value: "classic", label: "Classic" },
  { value: "acrylic", label: "Acrylic" },
  { value: "oil", label: "Oil" },
  { value: "watercolor", label: "Watercolor" },
];

export const isSubjectTreeNodeStyle = (
  value: string | null,
): value is SubjectTreeNodeStyle =>
  value === "classic" || value === "acrylic" || value === "oil" || value === "watercolor";

export const readStoredSubjectTreeStyle = (): SubjectTreeNodeStyle => {
  if (typeof window === "undefined") {
    return DEFAULT_SUBJECT_TREE_STYLE;
  }

  const savedStyle = window.localStorage.getItem(SUBJECT_TREE_STYLE_STORAGE_KEY);
  return isSubjectTreeNodeStyle(savedStyle) ? savedStyle : DEFAULT_SUBJECT_TREE_STYLE;
};

export const persistSubjectTreeStyle = (style: SubjectTreeNodeStyle): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SUBJECT_TREE_STYLE_STORAGE_KEY, style);
};

const stableHash = (value: string): number => {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const getSubjectStableSeed = (subjectId: number, title = "", depth = 0): number =>
  stableHash(`${subjectId}:${title.trim().toLowerCase()}:${depth}`);

const formatVariantNumber = (variant: number): string => String(variant).padStart(2, "0");

type SubjectTreeBrushProperties = CSSProperties & {
  "--subject-brush-image": string;
};

const getBrushImageUrl = (
  styleName: Exclude<SubjectTreeNodeStyle, "classic">,
  variant: number,
): string => `url("/subject-brushes/${styleName}-${formatVariantNumber(variant)}.png")`;

const getActiveBrushImageUrl = (style: SubjectTreeNodeStyle, variant: number): string =>
  style === "classic" ? "none" : getBrushImageUrl(style, variant);

export const getSubjectTreeBrushStyle = (
  subjectId: number,
  title = "",
  depth = 0,
  style: SubjectTreeNodeStyle = DEFAULT_SUBJECT_TREE_STYLE,
): SubjectTreeBrushProperties => {
  const seed = getSubjectStableSeed(subjectId, title, depth);
  const variant = (seed % SUBJECT_TREE_BRUSH_VARIANT_COUNT) + 1;

  return {
    "--subject-brush-image": getActiveBrushImageUrl(style, variant),
  };
};
